import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "@/assets/landingpage/poweredlogos/logo.png";
import whiteBall from "@/assets/landingpage/videosection/whiteBall.svg";
import white1 from "@/assets/landingpage/videosection/white1.svg";
import white2 from "@/assets/landingpage/videosection/white2.svg";
import white3 from "@/assets/landingpage/videosection/white3.svg";
import colorBall from "@/assets/landingpage/videosection/color-Ball.svg";
import colorShield from "@/assets/landingpage/videosection/color-shield.svg";
import colorCrown from "@/assets/landingpage/videosection/color-crown.svg";
import colorTrophy from "@/assets/landingpage/videosection/color-trophy.svg";
import { motion } from "framer-motion";
import { landingMotionInView } from "@/utils/landingMotion";

/** `iconInactive` = simple/white asset, `iconActive` = full-color asset (center highlight). */
export type PartnershipCarouselItem = {
  id: string;
  iconInactive: string;
  iconActive: string;
  label: string;
};

function usePartnershipSlides(): PartnershipCarouselItem[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        id: "fxtm",
        iconInactive: whiteBall,
        iconActive: colorBall,
        label: t("Exclusive Brokerage Partnership with FXTM"),
      },
      {
        id: "liquidity",
        iconInactive: white1,
        iconActive: colorShield,
        label: t("Fast execution & deep institutional liquidity"),
      },
      {
        id: "meta",
        iconInactive: white2,
        iconActive: colorCrown,
        label: t("Exclusive US MetaTrader Brokerage Access"),
      },
      {
        id: "top3",
        iconInactive: white3,
        iconActive: colorTrophy,
        label: t("Top 3 Prop Firms of 2025"),
      },
    ],
    [t]
  );
}

const SCROLL_PX_PER_SEC = 28;

export default function PartnershipFunnelSection() {
  const { t } = useTranslation();
  const slides = usePartnershipSlides();

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const translateRef = useRef(0);
  const segmentWidthRef = useRef(0);
  const rafRef = useRef<number>(0);
  const lastTsRef = useRef<number | null>(null);

  /**
   * Loop **indices** (0 … loopSlides.length-1), not slide ids — duplicated slides share the same id
   * but must highlight/reset independently so the second copy enters un-gradient.
   */
  const [highlightedIndices, setHighlightedIndices] = useState<Set<number>>(
    () => new Set()
  );

  const loopSlides = useMemo(() => [...slides, ...slides], [slides]);
  const setSlideRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    slideRefs.current[index] = el;
  }, []);

  const measureSegmentWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const half = track.scrollWidth / 2;
    if (half > 0) segmentWidthRef.current = half;
  }, []);

  /** Closest slide to center: once its midpoint reaches/passes the viewport center, lock gradient until loop wrap. */
  const updateHighlightPastCenter = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const vp = viewport.getBoundingClientRect();
    const centerX = vp.left + vp.width / 2;

    let bestIndex = -1;
    let bestDist = Infinity;
    let bestIx = 0;

    slideRefs.current.forEach((el, i) => {
      if (!el) return;
      const slide = loopSlides[i];
      if (!slide) return;
      const r = el.getBoundingClientRect();
      const ix = r.left + r.width / 2;
      const d = Math.abs(ix - centerX);
      if (d < bestDist) {
        bestDist = d;
        bestIndex = i;
        bestIx = ix;
      }
    });

    // Slides move right → left. Activate only once the slide midpoint reaches the center
    // (or has moved past it to the left), so it won't turn gradient early.
    if (bestIndex >= 0 && bestIx <= centerX) {
      setHighlightedIndices((prev) => {
        if (prev.has(bestIndex)) return prev;
        const next = new Set(prev);
        next.add(bestIndex);
        return next;
      });
    }
  }, [loopSlides]);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      measureSegmentWidth();
    });
    if (trackRef.current) ro.observe(trackRef.current);
    measureSegmentWidth();
    return () => ro.disconnect();
  }, [measureSegmentWidth, loopSlides.length]);

  useEffect(() => {
    const tick = (now: number) => {
      if (lastTsRef.current == null) lastTsRef.current = now;
      const dt = (now - lastTsRef.current) / 1000;
      lastTsRef.current = now;

      const track = trackRef.current;
      const seg = segmentWidthRef.current;
      if (track && seg > 0) {
        translateRef.current -= SCROLL_PX_PER_SEC * dt;
        if (translateRef.current <= -seg) {
          translateRef.current += seg;
          setHighlightedIndices(new Set());
        }
        track.style.transform = `translate3d(${translateRef.current}px,0,0)`;
      }

      updateHighlightPastCenter();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, [updateHighlightPastCenter, loopSlides.length]);

  const gradientTextClass =
    "bg-gradient-to-r from-[#29D1D1] to-[#D6FF3B] bg-clip-text italic text-transparent";
  const mutedTextClass = "text-white/45 italic";

  return (
    <div className="relative bg-dark font-nexa text-white lg:mt-0">
      <main className="mx-auto h-fit px-4 xl:px-0">
        <div className="mx-auto max-w-[1020px] text-center">
          <div className="flex justify-center">
            {/* subtitle The lading prop firm */}
            <motion.div
              className="w-fit h-auto p-[1.5px] rounded-[20px] bg-gradient-to-r from-[#1CCDE6] to-[#DBD633] flex justify-center items-center"
              {...landingMotionInView}
            >
              <div className="w-fit px-3 bg-black text-dark rounded-[20px] p-1 flex items-center justify-center">
                <span
                  className="bg-clip-text text-[12px] sm:text-[13px] xl:text-base text-transparent font-nexa font-bold uppercase"
                  style={{
                    backgroundImage:
                      "linear-gradient(to left, #D7D536, #20CDE2)",
                    display: "inline-block",
                  }}
                >
                  {("Relation built on trust")}
                </span>
              </div>
            </motion.div>
          </div>
          <motion.h2
            className="font-nexa pt-2 text-[32px] font-normal leading-tight tracking-tight md:text-[56px]"
            {...landingMotionInView}
          >
            {t("Built on Trust.  ")}
            <br />
            {t("Powered by ")}{" "}
            <span className="bg-gradient-to-r from-[#D6E242] via-[#9DDF78] to-[#79DDC7] bg-clip-text italic text-transparent">
              {t("Proven Partnerships.")}
            </span>
          </motion.h2>
          <motion.p
            className="mx-auto max-w-[780px] pt-1 font-nexa text-lg font-normal tracking-tight text-white/50 md:text-xl"
            {...landingMotionInView}
          >
            {t(
              "URFX’s credibility is reinforced by elite brokerage alliances and global recognition. Join a platform that’s ranked among the best and connected to the world’s top trading infrastructures."
            )}
          </motion.p>
        </div>

        <motion.div
          className="relative mx-auto mt-10 md:w-[86%]"
          {...landingMotionInView}
        >
          <div className="pointer-events-none absolute left-[-20%] top-1/2 z-[5] h-[400px] w-[100px] -translate-y-1/2 rounded-full  blur-[50px] bg-black sm:w-[200px] md:blur-[70px] lg:-left-24 lg:w-[300px]" />
          <div className="pointer-events-none absolute right-[-20%] top-1/2 z-[5] h-[400px] w-[100px] -translate-y-1/2 rounded-full  blur-[50px] bg-black sm:w-[200px] md:blur-[70px] lg:-right-24 lg:w-[300px]" />

          {/* Center logo — not part of carousel; tweak position/size here */}
          <motion.div
            className="flex justify-center items-center"
            {...landingMotionInView}
          >
            <div className="pointer-events-none w-[200px] md:w-[300px]    relative z-20 flex  items-center justify-center ">
              <img
                src={logo}
                alt="logoURFX logo"
                className="gradient-shadow relative z-50 w-[200px] object-cover md:w-[310px]"
              />
            </div>
          </motion.div>


          <div
            ref={viewportRef}
            className="relative z-0 -mt-48 overflow-hidden py-6 md:-mt-64 max-w-[1400px] mx-auto"
          >
            <div
              ref={trackRef}
              className="flex w-max items-center gap-12 md:gap-10 will-change-transform"
              style={{ transform: "translate3d(0,0,0)" }}
            >
              {loopSlides.map((slide, i) => {
                const isActive = highlightedIndices.has(i);
                const iconSrc = isActive
                  ? slide.iconActive || slide.iconInactive
                  : slide.iconInactive || slide.iconActive;

                return (
                  <div
                    key={`${slide.id}-${i}`}
                    ref={setSlideRef(i)}
                    className="flex w-[200px] shrink-0 flex-col items-center text-center md:w-[260px]"
                  >
                    <div
                      className={`relative flex h-[72px] w-[72px] items-center justify-center md:h-[100px] md:w-[100px] ${isActive
                          ? "opacity-100 drop-shadow-[0_0_28px_rgba(214,255,59,0.35)]"
                          : "opacity-55"
                        }`}
                    >
                      {iconSrc ? (
                        <img
                          src={iconSrc}
                          alt="partnership icon"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full border border-white/25 bg-white/5 md:h-16 md:w-16" />
                      )}
                    </div>
                    <p
                      className={`mt-3 px-1 font-creato font-normal text-base leading-snug md:text-lg ${isActive ? gradientTextClass : mutedTextClass
                        }`}
                    >
                      {slide.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
